<template>
  <el-dialog
    :visible.sync="isDialogOpen"
    width="500px"
    :show-close="true"
    class="egl-dialog-elm linkage-csv-dialog"
    :close-on-click-modal="false"
    :title="$locale('schedule.linkageCsvExport')"
  >
    <el-form class="egl-elm" :model="form" ref="linkageCsvForm" :rules="formRules">
      <el-form-item :label="$locale('schedule.linkageTarget')" prop="target">
        <el-radio-group v-model="form.target">
          <el-radio :label="TARGET_TMS">{{ $locale("schedule.linkageTargetTms") }}</el-radio>
          <el-radio :label="TARGET_CINEMAPHONE">{{ $locale("schedule.linkageTargetCinemaphone") }}</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item v-if="form.target === TARGET_CINEMAPHONE" :label="$locale('schedule.exportFile')" prop="files">
        <el-checkbox-group v-model="form.files">
          <el-checkbox :label="FILE_TITLE">title.csv</el-checkbox>
          <el-checkbox :label="FILE_SCHEDULE">schedule.csv</el-checkbox>
        </el-checkbox-group>
      </el-form-item>
      <el-form-item :label="$locale('schedule.dateRange')" prop="dateRange">
        <el-date-picker
          v-model="form.dateRange"
          type="daterange"
          range-separator="~"
          :start-placeholder="'開始日'"
          :end-placeholder="'終了日'"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
        />
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button class="egl-btn-white" @click="isDialogOpen = false">
        {{ $locale("global.cancel") }}
      </el-button>
      <el-button class="egl-btn-yellow" :loading="downloading" @click="handleDownload">
        {{ $locale("schedule.downloadCsv") }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import Encoding from "encoding-japanese";
import JSZip from "jszip";
import { exportBoardmediaCsv, exportCinemaphoneCsv } from "@/helpers/service/api/schedule.js";

const TARGET_TMS = "tms";
const TARGET_CINEMAPHONE = "cinemaphone";
const FILE_TITLE = "title";
const FILE_SCHEDULE = "schedule";

export default {
  name: "LinkageCsvDialog",
  data() {
    return {
      TARGET_TMS,
      TARGET_CINEMAPHONE,
      FILE_TITLE,
      FILE_SCHEDULE,
      isDialogOpen: false,
      downloading: false,
      pickStart: null,
      form: {
        target: TARGET_TMS,
        files: [FILE_TITLE, FILE_SCHEDULE],
        dateRange: [],
      },
      formRules: {
        files: [{ type: "array", required: true, message: "選択してください", trigger: "change" }],
        dateRange: [
          { required: true, message: "選択してください", trigger: "change" },
          { validator: this.validateDateRange, trigger: "change" },
        ],
      },
      pickerOptions: {
        onPick: ({ minDate }) => {
          // 记录第一次选中的日期，用于 disabledDate 限制范围
          this.pickStart = minDate ? new Date(minDate.getTime()) : null;
        },
        disabledDate: time => {
          // 选了开始日期后，限制结束日期在31天内
          if (this.pickStart) {
            const maxEnd = this.pickStart.getTime() + 31 * 24 * 60 * 60 * 1000;
            return time.getTime() > maxEnd;
          }
          return false;
        },
      },
    };
  },
  methods: {
    showDialog() {
      this.isDialogOpen = true;
      this.form.target = TARGET_TMS;
      this.form.files = [FILE_TITLE, FILE_SCHEDULE];
      this.form.dateRange = [];
      this.pickStart = null;
    },
    validateDateRange(_rule, value, callback) {
      if (value && value.length === 2) {
        const diffDays = (new Date(value[1]).getTime() - new Date(value[0]).getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays > 31) {
          callback(new Error(this.$locale("schedule.dateRangeMax31Days")));
          return;
        }
      }
      callback();
    },
    handleDownload() {
      this.$refs.linkageCsvForm.validate(valid => {
        if (!valid) { return; }

        this.downloading = true;
        const cinemaId = window.localStorage.getItem("cinemaId") || "";
        const startDate = this.$moment(this.form.dateRange[0]).valueOf();
        const endDate = this.$moment(this.form.dateRange[1]).valueOf() + 24 * 60 * 60 * 1000 - 1;
        const params = { cinemaId, startDate, endDate };

        const task = this.form.target === TARGET_CINEMAPHONE
          ? this.downloadCinemaphone(params)
          : this.downloadTms(params);

        task
          .then(downloaded => {
            if (downloaded) {
              this.isDialogOpen = false;
            } else {
              this.$alert(this.$locale("schedule.noDataForExport"), {
                showClose: false,
              });
            }
          })
          .catch(() => {
            this.$alert("エクスポートに失敗しました", {
              showClose: false,
            });
          })
          .finally(() => {
            this.downloading = false;
          });
      });
    },
    // 所选期间，用于文件名：<开始YYYYMMDD>-<结束YYYYMMDD>
    fileNameRange() {
      const [rangeStart, rangeEnd] = this.form.dateRange;
      return `${this.$moment(rangeStart).format("YYYYMMDD")}-${this.$moment(rangeEnd).format("YYYYMMDD")}`;
    },
    saveBlob(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    downloadTms(params) {
      return exportBoardmediaCsv(params).then(res => {
        if (!(res.success && res.data && res.data.length > 0)) {
          return false;
        }
        this.generateAndDownloadTmsCsv(res.data);
        return true;
      });
    },
    generateAndDownloadTmsCsv(data) {
      const csvData = data.map(item => ({
        ID: item.id,
        Cinema_Server: item.cinemaServer,
        Screen_bytNum: item.screenBytNum,
        Film_StrCode: item.filmStrCode,
        Film_Title: item.filmTitle,
        Film_Date: item.filmDate,
        Film_DurMinutes: item.filmDurMinutes,
        Film_DurHours: item.filmDurHours,
        Seats_Sold: item.seatsSold,
        Seats_Ava: item.seatsAva,
        Film_StrCensor: item.filmStrCensor,
      }));

      const fields = [
        { label: "ID", value: "ID" },
        { label: "Cinema_Server", value: "Cinema_Server" },
        { label: "Screen_bytNum", value: "Screen_bytNum" },
        { label: "Film_StrCode", value: "Film_StrCode" },
        { label: "Film_Title", value: "Film_Title" },
        { label: "Film_Date", value: "Film_Date" },
        { label: "Film_DurMinutes", value: "Film_DurMinutes" },
        { label: "Film_DurHours", value: "Film_DurHours" },
        { label: "Seats_Sold", value: "Seats_Sold" },
        { label: "Seats_Ava", value: "Seats_Ava" },
        { label: "Film_StrCensor", value: "Film_StrCensor" },
      ];

      // TMS 导入规格：Tab 分隔、无引号、UTF-8 无 BOM（DEV-14801）
      // json2csv 必定会加引号，因此固定 11 列的本 CSV 手动拼接
      // BOM 会粘在首列表头上导致 TMS 无法取込，故不添加
      const header = fields.map(field => field.label).join("\t");
      const rows = csvData.map(row => fields.map(field => row[field.value]).join("\t"));
      const result = [header, ...rows].join("\r\n");
      const csvContent =
        "data:text/tab-separated-values;charset=utf-8," + encodeURIComponent(result);
      const link = document.createElement("a");
      link.href = csvContent;
      // 文件名为 TMS 指定格式：broadmedia_schedule_<开始YYYYMMDD>-<结束YYYYMMDD>
      // 同一上映场次再次取込时文件名必须一致，
      // 故使用所选期间而非下载时刻（DEV-14801）
      link.download = `broadmedia_schedule_${this.fileNameRange()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadCinemaphone(params) {
      return exportCinemaphoneCsv(params).then(res => {
        if (!(res.success && res.data && res.data.schedules && res.data.schedules.length > 0)) {
          return false;
        }
        return this.generateAndDownloadCinemaphoneCsv(res.data).then(() => true);
      });
    },
    // シネマフォン取込规格：Shift_JIS、逗号分隔、LF 换行、有表头（DEV-14781）
    // 字段含逗号、双引号、空格或换行时加双引号，内部双引号写成 ""（与样本一致）
    toCinemaphoneCsv(header, rows) {
      const escape = value => {
        const text = value === null || value === undefined ? "" : String(value);
        return /[",\s]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      };
      const lines = [header, ...rows].map(cols => cols.map(escape).join(","));
      const content = lines.join("\n") + "\n";
      // Shift_JIS 无法表示的字符（emoji 等）由 encoding-japanese 默认替换为 ?
      const sjisArray = Encoding.convert(Encoding.stringToCode(content), { to: "SJIS", from: "UNICODE" });
      return new Uint8Array(sjisArray);
    },
    generateAndDownloadCinemaphoneCsv(data) {
      const files = [];
      // 文件名为シネマフォン指定：title.csv / schedule.csv
      if (this.form.files.includes(FILE_TITLE)) {
        files.push({
          name: "title.csv",
          bytes: this.toCinemaphoneCsv(
            ["上映作品ID", "上映作品名", "上映作品備考"],
            data.titles.map(item => [item.titleId, item.titleName, item.remark]),
          ),
        });
      }
      if (this.form.files.includes(FILE_SCHEDULE)) {
        files.push({
          name: "schedule.csv",
          bytes: this.toCinemaphoneCsv(
            ["上映作品ID", "対象日", "開始時刻", "終了時刻", "スクリーンID", "スケジュール備考"],
            data.schedules.map(item => [
              item.titleId, item.targetDate, item.startTime, item.endTime, item.screenId, item.remark,
            ]),
          ),
        });
      }

      if (files.length === 1) {
        this.saveBlob(new Blob([files[0].bytes], { type: "text/csv" }), files[0].name);
        return Promise.resolve();
      }
      // 两个文件都导出时打包为 zip，zip 内文件名保持シネマフォン指定的名称
      const zip = new JSZip();
      files.forEach(file => zip.file(file.name, file.bytes));
      return zip.generateAsync({ type: "blob" }).then(blob => {
        this.saveBlob(blob, `cinemaphone_${this.fileNameRange()}.zip`);
      });
    },
  },
};
</script>
